// Core
import React, { useState, useRef, useEffect, useContext } from "react"
import gsap from "gsap"
import { ScrollToPlugin } from "gsap/all"
import { isMobile } from "react-device-detect"
import { maskIn } from "./../../utils/masks"
import { localiseString } from "../../utils/localiseString"

// Components
import ScrollSection from "../locomotiveScroll/scrollSection"
import Video from "./../video"
import { SlideMaskText } from "../slideMaskText"

// Images
import Soundstage from "./images/soundstage.inline.svg"
import SoundstageWide from "./images/soundstagewide.inline.svg"
import PlayIcon from "./images/playIcon.inline.svg"

// Style
import styles from "./hero.module.scss"
import Portal from "../portal"
import Notify from "../notify"
import { ContextDispatch, ContextState } from "../../context/Context"

gsap.registerPlugin(ScrollToPlugin)

const Hero = ({
  title,
  description,
  soundStage = false,
  mobileImage,
  desktopImage,
  loop,
  vimeo,
  externalLink,
  scrolledEvent,
  openMenu,
  closeMenu,
  expired,
  episodeNumber,
  seasonNumber,
  runtime,
  scheduledDateString,
  scheduledDate,
  reveal = false,
}) => {
  const store = useContext(ContextState)
  const storeDispatch = useContext(ContextDispatch)
  const [loaded, setLoaded] = useState(false)
  const [beginLoop, setBeginLoop] = useState(false)
  const [playing, setPlaying] = useState(false)
  const [showNotify, setShowNotify] = useState(false)

  const wrap = useRef()
  let playLoopTimeout = null,
    introDelayTimeout = null

  useEffect(() => {
    setLoaded(true)
    if (loop) {
      playLoopTimeout = setTimeout(playLoop, 200)
    } else {
      maskIn()
    }

    setTimeout(() => {
      if (!isMobile) {
        window.scroll.on("call", (value, way, obj) => {
          if (value === "hero") scrolledEvent(way)
        })
      }
    }, 200)

    introDelayTimeout = setTimeout(() => {
      if (wrap.current) wrap.current.setAttribute("delay", "false")
    }, 2000)

    return () => {
      if (playLoopTimeout) clearTimeout(playLoopTimeout)
      if (introDelayTimeout) clearTimeout(introDelayTimeout)
    }
  }, [])

  useEffect(() => {
    document.body.setAttribute("video-playing", playing)

    if (playing) {
      document.body.classList.add("no-scroll")
      closeMenu && closeMenu()
    } else if (document.body.classList.contains("no-scroll")) {
      document.body.classList.remove("no-scroll")
      openMenu && openMenu()
    }
  }, [playing])

  const scrollToIntro = () => {
    let target = wrap.current.parentNode.nextSibling

    if (isMobile) {
      gsap.to(window, { duration: 1, scrollTo: { y: target } })
    } else {
      window.scroll.scrollTo(target)
    }
  }

  const play = () => {
    let target = document.querySelector("#ep-hero")

    if (isMobile) {
      gsap.to(window, { duration: 1, scrollTo: { y: target } })
    } else {
      window.scroll.scrollTo(target)
    }

    setPlaying(true)
  }

  const pause = () => {
    setPlaying(false)
    if (!store.donatePromptShown) {
      storeDispatch({ type: "SHOW_DONATE_MODAL" })
    }
  }

  const playLoop = () => {
    setBeginLoop(true)
  }

  const isLive = scheduledDate

  return (
    <ScrollSection zindex={11}>
      <div
        data-scroll
        data-scroll-call="hero"
        data-scroll-offset={`${reveal ? "0, 10%" : "0, 50%"}`}
        data-scroll-speed={`${reveal ? "4" : null}`}
      >
        <div
          className={`${styles.wrap} ${loaded ? styles.loaded : ""} ${
            beginLoop ? styles.looping : ""
          } ${playing ? styles.playing : ""} ${reveal ? styles.reveal : ""}`}
          id="ep-hero"
          ref={wrap}
        >
          <div className={styles.videoWrap} data-scroll data-scroll-speed="-1">
            {!expired && vimeo && !externalLink && (
              <div className={styles.closeBtn} onClick={pause}>
                <span />
                <span />
              </div>
            )}

            {!expired && vimeo && !externalLink && (
              <div className={styles.videoInner}>
                <Video vidID={vimeo} fill={true} playState={playing} />
              </div>
            )}

            {loop && (
              <div className={styles.videoLoop}>
                <Video
                  vidID={loop}
                  playState={beginLoop}
                  autoplay
                  loop
                  muted
                  fill
                  stretch
                  playCallback={() => maskIn()}
                />
              </div>
            )}

            {soundStage && (
              <div className={styles.soundstage}>
                <p className={`${styles.mono} mono`}>{title}</p>
                {<Soundstage />}
                <SoundstageWide />
              </div>
            )}

            {desktopImage ? (
              <picture className={styles.poster}>
                <source
                  media="(min-width: 1024px)"
                  srcSet={desktopImage.fluid.src}
                />
                <img
                  src={mobileImage.fluid.src}
                  alt={desktopImage.fluid.description}
                />
              </picture>
            ) : (
              <div className={styles.poster}></div>
            )}
          </div>

          {vimeo && (
            <div className={styles.heroContent}>
              <div className={`${styles.heroContentInner} container`}>
                {
                  seasonNumber > 0 ? (
                    <p className={`${styles.heroEpisode} mono`}>
                      <strong>{localiseString("Season")} {seasonNumber}</strong> - {localiseString("EP")}{episodeNumber}
                    </p>
                  ) : (
                    <p className={`${styles.heroEpisode} mono`}>
                      <strong>{localiseString("Bonus")}</strong> - {localiseString("EP")}{episodeNumber}
                    </p>
                  )
                }

                <h1 className={`${styles.heroTitle} h3`}>{title}</h1>

                <p className={styles.heroDescription}>
                  {expired
                    ? `${localiseString("This concert’s streaming date has passed. Enjoy its accompanying essay, interview, special performances and more below.")}`
                    : description.seoDescription}
                </p>

                {isLive && runtime && !expired && (
                  <p className={styles.heroRuntime}>{localiseString("Runtime")} - {runtime} {localiseString("mins")}</p>
                )}

                {expired && scheduledDate && (
                  <p className={styles.heroRuntime}>
                    {localiseString("This episode was released on")} {scheduledDateString}
                  </p>
                )}

                {!isLive && (
                  <>
                    <button
                      className={`${styles.notifyBtn} button button--outline`}
                      onClick={() => setShowNotify(true)}
                    >
                      <SlideMaskText
                        text={`${localiseString("Launches")} ${scheduledDateString}`}
                      />
                    </button>
                    <Portal>
                      <Notify
                        open={showNotify}
                        closeEvent={() => setShowNotify(false)}
                      />
                    </Portal>
                  </>
                )}

                {isLive && externalLink ? (
                  <a
                    href={externalLink}
                    className={`${styles.heroButton} button button--filled`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <PlayIcon />
                    <SlideMaskText text={localiseString("Watch Now")} />
                  </a>
                ) : (
                  isLive &&
                  vimeo &&
                  !expired && (
                    <button
                      className={`${styles.heroButton} button button--filled`}
                      onClick={play}
                    >
                      <PlayIcon />
                      <SlideMaskText text={localiseString("Watch Now")} />
                    </button>
                  )
                )}
              </div>
            </div>
          )}

          {isLive && !reveal && (
            <div className={styles.explore}>
              <button className={styles.exploreButton} onClick={scrollToIntro}>
                <SlideMaskText text={localiseString("Discover More")} />
                <svg
                  width="19"
                  height="12"
                  viewBox="0 0 19 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M1 1l8.5 9L18 1" stroke="#E6E5E1" strokeWidth="2" />
                </svg>
              </button>
            </div>
          )}
        </div>

        {reveal && <div className={styles.heroGradient}></div>}
      </div>
    </ScrollSection>
  )
}

export default Hero
